import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { acknowledgeAlert } from '../lib/api';
import { Bell, AlertTriangle, CheckCircle, Loader2, BellOff } from 'lucide-react';

interface Alert {
  id: number;
  mango_id: number;
  message: string;
  alarm_level: string;
  active_timestamp: string;
  acknowledged: boolean;
}

const AlertsWidget: React.FC = () => {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState<number | null>(null);

  // 1. Funci�n para cargar alertas (solo las no reconocidas)
  const fetchAlerts = async () => {
    const { data, error } = await supabase
      .schema('radix')
      .from('active_alerts')
      .select('*')
      .eq('acknowledged', false)
      .order('active_timestamp', { ascending: false });

    if (error) {
      console.error("Error Supabase:", error);
    } else if (data) {
      setAlerts(data);
    }
  };

  // 2. Suscripci�n Realtime para actualizar la lista autom�ticamente
  useEffect(() => {
    fetchAlerts();

    const channel = supabase
      .channel('db-changes')
      .on(
        'postgres_changes',
        { 
          event: '*', // Escucha cualquier cambio (UPDATE de n8n o nuevos INSERTs)
          schema: 'radix', 
          table: 'active_alerts' 
        },
        () => {
          fetchAlerts(); // Recarga la lista cuando hay cambios en la DB
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // 3. Funci�n para enviar el reconocimiento a n8n
  const handleAcknowledge = async (mangoId: number) => {
    setLoading(mangoId);
    try {
      await acknowledgeAlert(mangoId);
      // No forzamos setAlerts aqu�; el Realtime lo har� cuando n8n actualice la DB
    } catch (error) {
      console.error('Error al reconocer alerta:', error);
      alert('Error en el servidor al procesar el reconocimiento.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden mb-6">
      {/* Encabezado Estilo BMS */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-blue-50 rounded-md">
            <Bell className="text-blue-600 w-5 h-5" />
          </div>
          <h3 className="text-lg font-bold text-gray-800 tracking-tight">Centro de Alertas</h3>
        </div>
        <div className="flex items-center gap-2">
          <span className={`px-2 py-0.5 rounded-full text-xs font-bold font-mono transition-colors ${
            alerts.length > 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
          }`}>
            {alerts.length} {alerts.length === 1 ? 'PENDIENTE' : 'PENDIENTES'}
          </span>
        </div>
      </div>

      {/* Contenedor de Alertas con Scroll */}
      <div className="p-4 max-h-[400px] overflow-y-auto custom-scrollbar">
        {alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-gray-400">
            <div className="bg-green-50 p-4 rounded-full mb-3">
              <CheckCircle className="w-8 h-8 text-green-500 opacity-60" />
            </div>
            <p className="text-sm font-medium">No hay eventos activos en la infraestructura</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {alerts.map((alert) => (
              <div 
                key={alert.id} 
                className={`flex items-center justify-between p-3 rounded-lg border shadow-sm transition-all hover:shadow-md ${
                  alert.alarm_level === 'URGENT' 
                    ? 'bg-red-50/30 border-red-100' 
                    : 'bg-amber-50/30 border-amber-100'
                }`}
              >
                <div className="flex gap-3">
                  <div className={`mt-0.5 ${alert.alarm_level === 'URGENT' ? 'text-red-500' : 'text-amber-500'}`}>
                    <AlertTriangle className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase ${
                        alert.alarm_level === 'URGENT' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {alert.alarm_level}
                      </span>
                    </div>
                    <p className="text-[13px] font-semibold text-gray-800 leading-snug">
                      {alert.message}
                    </p>
                    <p className="text-[10px] text-gray-500 mt-2 font-mono italic">
                      {new Date(alert.active_timestamp).toLocaleString()}
                    </p>
                  </div>
                </div>

                <button
                  onClick={() => handleAcknowledge(alert.mango_id)}
                  disabled={loading === alert.mango_id}
                  className="ml-2 bg-white hover:bg-gray-50 text-gray-400 hover:text-blue-600 border border-gray-200 p-2.5 rounded-lg shadow-sm transition-all flex items-center justify-center min-w-[42px]"
                  title="Marcar como enterado"
                >
                  {loading === alert.mango_id ? (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  ) : (
                    <BellOff className="w-5 h-5" />
                  )}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AlertsWidget;